import ErrorComponent from "@/components/Error";
import { Spinner } from "@/components/Spinner";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import createAxios from "@/lib/axios";
import { format, parse } from "date-fns";
import { ScrollArea } from "@/components/ui/scroll-area";
import { CalendarDays, ClockIcon, History as HistoryIcon } from "lucide-react";

type QASet = {
  _id: string;
  qaEntriesIds: string[];
  userId: string;
  subjectName: string;
  createdAt: string;
};

export default function History({ userID }: { userID: string }) {
  const {
    data: sets,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["sets", userID],
    queryFn: async () => {
      const client = await createAxios();
      const { data } = await client.get<QASet[]>(
        `${import.meta.env.VITE_BACKEND_URL}/qa-sets`,
      );
      const sets = splitQASetsByTime(data ?? []);
      return sets;
    },
  });

  if (isLoading)
    return (
      <div className="flex h-full w-full flex-col bg-blue-50">
        <div className="border-b border-blue-100 bg-blue-100 p-4">
          <h2 className="flex items-center text-lg font-semibold text-secondary">
            <HistoryIcon size={18} className="mr-2" /> Quiz History
          </h2>
        </div>
        <div className="flex h-full items-center justify-center p-6 text-secondary">
          <Spinner /> <span className="ml-2">Loading history...</span>
        </div>
      </div>
    );

  if (error) return <ErrorComponent error={error} />;

  const hasQuizzes =
    sets &&
    (sets.today.length > 0 ||
      sets.yesterday.length > 0 ||
      sets.lastWeek.length > 0);

  if (!hasQuizzes) {
    return (
      <div className="flex h-full w-full flex-col bg-blue-50">
        <div className="border-b border-blue-100 bg-blue-100 p-4">
          <h2 className="flex items-center text-lg font-semibold text-secondary">
            <HistoryIcon size={18} className="mr-2" /> Quiz History
          </h2>
        </div>
        <div className="flex h-full items-center justify-center p-6 text-secondary">
          No previous quizzes found
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col bg-blue-50">
      <div className="border-b border-blue-100 bg-blue-100 p-4">
        <h2 className="flex items-center text-lg font-semibold text-secondary">
          <HistoryIcon size={18} className="mr-2" /> Quiz History
        </h2>
      </div>
      <ScrollArea className="flex-1 px-4 py-4">
        <div className="flex flex-col gap-6 pb-2">
          <Quizzes
            sets={sets.today}
            title="Today"
            icon={<ClockIcon size={16} className="text-secondary" />}
          />
          <Quizzes
            sets={sets.yesterday}
            title="Yesterday"
            icon={<CalendarDays size={16} className="text-secondary" />}
          />
          <Quizzes
            sets={sets.lastWeek}
            title="Last Week"
            icon={<CalendarDays size={16} className="text-secondary" />}
          />
        </div>
      </ScrollArea>
    </div>
  );
}

function Quizzes({
  sets,
  title,
  icon,
}: {
  sets: QASet[];
  title: string;
  icon: React.ReactNode;
}) {
  if (sets.length === 0) return null;

  return (
    <div className="space-y-3">
      <h3 className="flex items-center text-sm font-medium text-secondary">
        {icon}
        <span className="ml-1.5">{title}</span>
      </h3>
      <ul className="space-y-2">
        {sets.map((set) => (
          <li key={set._id}>
            <Quiz set={set} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function Quiz({ set }: { set: QASet }) {
  return (
    <Link
      to={"/dashboard/quiz/$id"}
      params={{ id: set._id }}
      className="block w-full"
    >
      <div
        className="flex h-auto w-full items-center justify-between rounded-md border border-blue-100 bg-white px-3 py-2 text-left shadow-sm transition-all hover:bg-gray-200 hover:shadow-md"
        aria-label={`Quiz on ${set.subjectName} from ${convertUTCToIST(set.createdAt)}`}
      >
        <div className="flex flex-col items-start">
          <span className="text-sm font-medium text-secondary">
            {convertUTCToIST(set.createdAt)}
          </span>
        </div>
        <Badge className="ml-2 max-w-[120px] truncate bg-blue-200 text-secondary hover:bg-blue-200">
          {set.subjectName}
        </Badge>
      </div>
    </Link>
  );
}

function splitQASetsByTime(sets: QASet[]) {
  const setsToday =
    sets.filter(
      (set) => new Date().getDate() - new Date(set.createdAt).getDate() === 0,
    ) || [];
  const setsYesterday =
    sets.filter(
      (set) => new Date().getDate() - new Date(set.createdAt).getDate() === 1,
    ) || [];
  const setsSinceLastWeek =
    sets
      .filter(
        (set) =>
          new Date().getDate() - new Date(set.createdAt).getDate() <= 7 &&
          !setsToday.includes(set) &&
          !setsYesterday.includes(set),
      )
      .slice(0, 5) || [];
  return {
    today: setsToday,
    yesterday: setsYesterday,
    lastWeek: setsSinceLastWeek,
  };
}

function convertUTCToIST(dateString: string) {
  const inputFormat = "yyyy-MM-dd HH:mm:ss";
  const date = parse(dateString, inputFormat, new Date());
  const outputFormat = "d MMM hh:mm a";
  return format(date, outputFormat);
}
